import React, {useState, useEffect, useMemo} from "react";
import {
    Switch, 
    Grid, 
    Button, 
    Box, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Paper, 
    TableSortLabel,
    Pagination,
    FormControl,
    Select,
    MenuItem,
    LinearProgress,
    Typography
} from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CircularProgress from '@mui/material/CircularProgress';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import "./BandTable.css";

const SwitchActivationCell = ({ row, activateBrand }) => {
    const {logoVersionArray} = row;
    const totalLogosNumber = logoVersionArray.length;
    const checkedLogosNumber = logoVersionArray.filter(logo => logo.status === 1).length;
    const [status, setStatus] = useState(checkedLogosNumber >= 1);
    const [isLoading, setIsLoading] = useState(
        logoVersionArray.filter(logo => logo.status === -1).length > 0
    );

    let color = 'default'
    if (checkedLogosNumber === totalLogosNumber) {
        color = 'primary'
    }
    if (
        (checkedLogosNumber < totalLogosNumber)
        && (checkedLogosNumber > 0)
    ) {
        color = 'warning'
    }
    
    // TypeScript compatibility for Switch color prop
    const switchColor = color === 'primary' ? 'primary' : color === 'warning' ? 'warning' : 'default'

    return (
        <>
            {isLoading
                ? <CircularProgress/>
                : <Switch
                    checked={status}
                    color={switchColor}
                    onChange={(event) => {
                        const check = event.target.checked;
                        activateBrand(check, row);
                        if (check) {
                            setIsLoading(true)
                        } else {
                            setStatus(false)
                        }
                    }}
                />
            }
        </>
    )
}


export default function BrandTable({
    items,
    openModalBrand,
    loading,
    activateBrand,
}) {
    const [pageSize, setPageSize] = useState(50);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortField, setSortField] = useState('computedName');
    const [sortDirection, setSortDirection] = useState('asc');

    // Sort items
    const sortedItems = useMemo(() => {
        const sorted = [...items].sort((a, b) => {
            let aVal = a[sortField];
            let bVal = b[sortField];
            
            if (sortField === 'computedName') {
                aVal = aVal?.toLowerCase() || '';
                bVal = bVal?.toLowerCase() || '';
            }
            
            if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
        return sorted;
    }, [items, sortField, sortDirection]);

    // Paginate items
    const paginatedItems = useMemo(() => {
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        return sortedItems.slice(startIndex, endIndex);
    }, [sortedItems, currentPage, pageSize]);

    const totalPages = Math.ceil(items.length / pageSize);

    const handleSort = (field) => {
        if (field === sortField) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const handlePageChange = (event, page) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (event) => {
        setPageSize(event.target.value);
        setCurrentPage(1);
    };

    const renderLogoImages = (row) => {
        const {logoVersionArray} = row;
        return logoVersionArray.map((logo, index) => {
            const statusColor = logo.status === 1 ? "green" : "red";
            const {meta} = logo;
            const showLoading = logo.status === -1;
            let showTagIcon = false;
            
            if (!!meta && !showLoading) {
                const metaLength = Object.keys(meta).length;
                if (metaLength > 1) showTagIcon = true;
                if (metaLength === 1) {
                    if (!meta['.name']) showTagIcon = true;
                }
            }

            return (
                <div
                    className={'BrandTableImgContainer'}
                    key={`${logo.name}-${index}`}
                    onClick={() => {
                        openModalBrand(row, index);
                    }}
                >
                    <img
                        src={logo.iconUrl}
                        alt={logo.name}
                    />
                    {showLoading ? (
                        <CircularProgress
                            size={15}
                            sx={{
                                position: 'absolute',
                                right: '5px',
                                bottom: '5px',
                            }}
                        />
                    ) : (
                        <div
                            className={`statusCircle ${statusColor}`}
                            onClick={ev => {
                                ev.preventDefault();
                                ev.stopPropagation();
                                activateBrand(
                                    logo.status !== 1,
                                    row,
                                    logo,
                                );
                            }}
                        />
                    )}

                    {showTagIcon && (
                        <LocalOfferIcon 
                            color={'primary'} 
                            sx={{
                                position: "absolute",
                                bottom: "5px",
                                right: 0,
                                fontSize: "11px",
                                left: "20px",
                            }}
                        />
                    )}
                </div>
            );
        });
    };

    return (
        <Box sx={{ width: "100%", height: "calc(100vh - 330px)" }}>
            {loading && <LinearProgress />}
            
            <TableContainer component={Paper} sx={{ height: 'calc(100% - 60px)' }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow sx={{ height: 'auto' }}>
                            <TableCell sx={{ width: 350, paddingTop: '8px', paddingBottom: '8px' }}>
                                <TableSortLabel
                                    active={sortField === 'computedName'}
                                    direction={sortField === 'computedName' ? (sortDirection === 'asc' ? 'asc' : 'desc') : 'asc'}
                                    onClick={() => handleSort('computedName')}
                                >
                                    Brand
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ paddingTop: '8px', paddingBottom: '8px' }}>
                                Logo Images
                            </TableCell>
                            <TableCell sx={{ width: 150, paddingTop: '8px', paddingBottom: '8px' }}>
                                Toggle
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedItems.map((row) => (
                            <TableRow 
                                key={row.tableId} 
                                sx={{ height: 100 }}
                            >
                                <TableCell>
                                    <Button
                                        fullWidth={true}
                                        sx={{
                                            textTransform: 'none',
                                        }}
                                        className={'d-flex'}
                                        onClick={() => openModalBrand(row, -1)}
                                    >
                                        <Grid container justifyContent={'space-between'}>
                                            <Grid item>
                                                {row.computedName}
                                            </Grid>
                                            <Grid item alignSelf={'flex-end'}>
                                                <OpenInNewIcon/>
                                            </Grid>
                                        </Grid>
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <div className={'BrandTable--logoRow'}>
                                        {renderLogoImages(row)}
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <SwitchActivationCell
                                        row={row}
                                        activateBrand={activateBrand}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                        value={pageSize}
                        onChange={handlePageSizeChange}
                        displayEmpty
                    >
                        <MenuItem value={10}>10 per page</MenuItem>
                        <MenuItem value={50}>50 per page</MenuItem>
                        <MenuItem value={100}>100 per page</MenuItem>
                    </Select>
                </FormControl>
                
                <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                />
                
                <Typography variant="body2" color="text.secondary">
                    {`${(currentPage - 1) * pageSize + 1}-${Math.min(currentPage * pageSize, items.length)} of ${items.length}`}
                </Typography>
            </Box>
        </Box>
    );
}
